import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AI_MODELS } from '@/components/AIModelSelector';
import { 
  Columns, 
  Copy, 
  Check, 
  Loader2, 
  Lock, 
  RefreshCw,
  Sparkles,
  ArrowRight,
  Eye,
  Bookmark,
  BookmarkCheck
} from 'lucide-react';

// Model access by plan for comparison feature
const COMPARE_ACCESS = {
  free: { allowed: false, maxModels: 0 },
  starter: { allowed: false, maxModels: 0 },
  growth: { allowed: true, maxModels: 2, blurAfter: 1 },  // 1 full + 1 blurred
  pro: { allowed: true, maxModels: 4, blurAfter: 4 }  // All visible
};

// Upgrade Modal Component
const CompareUpgradeModal = ({ isOpen, onClose, currentPlan }) => {
  const navigate = useNavigate();
  
  const targetPlan = currentPlan === 'growth' ? 'pro' : 'growth';
  const planDisplayNames = { growth: 'Growth', pro: 'Pro' };
  
  const handleUpgrade = () => {
    onClose();
    navigate('/settings', { state: { selectedPlan: targetPlan } });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Eye className="w-5 h-5 text-amber-500" />
            Unlock Side-by-Side Results
          </DialogTitle>
          <DialogDescription className="pt-2">
            Compare AI outputs to find the perfect prompt style for your needs.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-3">
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-lg flex items-center justify-center text-white">
                <Columns className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">Full Compare Access</p>
                <p className="text-sm text-muted-foreground">
                  {targetPlan === 'pro' 
                    ? 'See up to 4 AI results side-by-side' 
                    : 'Unlock model comparisons'}
                </p>
              </div>
            </div>
          </div>
          
          <ul className="text-sm text-muted-foreground space-y-1.5 pl-4">
            <li className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-green-500" />
              <span>Side-by-side prompt outputs</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-green-500" />
              <span>Find your preferred style instantly</span>
            </li>
            <li className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 text-green-500" />
              <span>One-click copy any result</span>
            </li>
          </ul>
        </div>
        
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>
            Maybe Later
          </Button>
          <Button onClick={handleUpgrade} className="gap-2">
            Upgrade to {planDisplayNames[targetPlan]}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Blurred Result Card
const BlurredResultCard = ({ model, onClick }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative bg-muted/30 rounded-xl p-4 border border-border overflow-hidden cursor-pointer group"
      onClick={onClick}
    >
      {/* Blurred Content */}
      <div className="blur-sm select-none pointer-events-none">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-lg">{model?.icon}</span>
          <span className="font-medium text-sm">{model?.name}</span>
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground mb-3">
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore...
        </p>
      </div>
      
      {/* Lock Overlay */}
      <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 transition-all group-hover:bg-background/70">
        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
          <Lock className="w-5 h-5 text-muted-foreground" />
        </div>
        <p className="text-xs font-medium text-center px-4">Unlock side-by-side results</p>
        <Button size="sm" variant="outline" className="mt-1 text-xs h-7">
          Upgrade to Pro
        </Button>
      </div>
    </motion.div>
  );
};

// Result Card Component
const ResultCard = ({ model, result, isSelected, onCopy, copied }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-muted/30 rounded-xl p-4 border transition-all ${
        isSelected ? 'border-foreground ring-1 ring-foreground/20' : 'border-border'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg">{model?.icon}</span>
          <span className="font-medium text-sm">{model?.name}</span>
        </div>
        {result?.highlight && (
          <span className="text-[10px] bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
            {result.highlight}
          </span>
        )}
      </div>
      
      <p className="text-xs leading-relaxed text-muted-foreground mb-3 min-h-[60px]">
        {result?.enhanced?.slice(0, 280)}{result?.enhanced?.length > 280 ? '...' : ''}
      </p>
      
      <Button
        variant="ghost"
        size="sm"
        className="w-full h-8 text-xs"
        onClick={() => onCopy(result?.enhanced, model?.id)}
      >
        {copied ? (
          <>
            <Check className="w-3 h-3 mr-1.5" />
            Copied
          </>
        ) : (
          <>
            <Copy className="w-3 h-3 mr-1.5" />
            Copy Result
          </>
        )}
      </Button>
    </motion.div>
  );
};

// Main CompareResults Component
const CompareResults = ({ 
  prompt, 
  style = 'general',
  selectedModel = 'gpt',
  onClose 
}) => {
  const { api, user, updateCredits } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  const userPlan = user?.plan || 'free';
  const access = COMPARE_ACCESS[userPlan] || COMPARE_ACCESS.free;
  const canSave = userPlan === 'pro';
  
  // Determine which models to compare based on plan
  const getModelsToCompare = () => {
    if (userPlan === 'pro') {
      return ['gpt', 'claude', 'gemini', 'mistral'].slice(0, 4);
    } else if (userPlan === 'growth') {
      return ['gpt', 'claude'];
    }
    return ['gpt'];
  };
  
  const modelsToCompare = getModelsToCompare();
  
  const handleCompare = async () => {
    if (!access.allowed) {
      setShowUpgradeModal(true);
      return;
    }
    
    if (!prompt?.trim()) {
      toast.error('Please enter a prompt first');
      return;
    }
    
    const creditCost = modelsToCompare.length * 2;
    if (user.credits < creditCost) {
      toast.error(`Insufficient credits. Compare requires ${creditCost} credits.`);
      return;
    }
    
    setLoading(true);
    setSaved(false); // Reset saved state on new comparison
    try {
      const response = await api.post('/ai/compare-results', {
        prompt: prompt.trim(),
        style,
        models: modelsToCompare
      });
      
      setResults(response.data);
      updateCredits(user.credits - response.data.credits_used);
      toast.success(`Compared ${Object.keys(response.data.comparisons).length} models!`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Comparison failed';
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleRegenerate = () => {
    setResults(null);
    setSaved(false);
    handleCompare();
  };
  
  const handleSaveToGallery = async () => {
    if (!canSave) {
      setShowUpgradeModal(true);
      return;
    }
    
    if (!results?.comparisons) {
      toast.error('No comparison results to save');
      return;
    }
    
    setSaving(true);
    try {
      await api.post('/ai/save-comparison', {
        original_prompt: prompt,
        style,
        comparisons: results.comparisons,
        models_used: results.models_used
      });
      
      setSaved(true);
      toast.success('Comparison saved to your gallery!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save comparison';
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        toast.error(message);
      }
    } finally {
      setSaving(false);
    }
  };
  
  const copyToClipboard = async (text, modelId) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    setCopied({ ...copied, [modelId]: true });
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied({ ...copied, [modelId]: false }), 2000);
  };
  
  // Locked state for non-eligible plans
  if (!access.allowed) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6">
          <CompareUpgradeModal 
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            currentPlan={userPlan}
          />
          
          <div className="text-center py-6">
            <div className="w-14 h-14 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-muted-foreground" />
            </div>
            <h3 className="font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Compare Results
            </h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-xs mx-auto">
              See which prompt performs best with side-by-side AI comparisons
            </p>
            <Button onClick={() => setShowUpgradeModal(true)}>
              Upgrade to Unlock
            </Button>
            <p className="text-xs text-muted-foreground/70 mt-3 italic">
              Better comparisons = better results
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardContent className="p-5">
        <CompareUpgradeModal 
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentPlan={userPlan}
        />
        
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Columns className="w-4 h-4 text-muted-foreground" />
            <h3 className="font-medium text-sm">Compare Results</h3>
            <span className="text-xs text-muted-foreground">
              See which prompt performs best
            </span>
          </div>
          
          {results && (
            <div className="flex items-center gap-2">
              {/* Save to Gallery - Pro only */}
              {canSave && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveToGallery}
                  disabled={saving || saved}
                  className="h-7 text-xs"
                  data-testid="save-comparison-btn"
                >
                  {saved ? (
                    <>
                      <BookmarkCheck className="w-3 h-3 mr-1.5 text-green-500" />
                      Saved
                    </>
                  ) : saving ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Bookmark className="w-3 h-3 mr-1.5" />
                      Save to Gallery
                    </>
                  )}
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRegenerate}
                disabled={loading}
                className="h-7 text-xs"
              >
                <RefreshCw className={`w-3 h-3 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                Regenerate All
              </Button>
            </div>
          )}
        </div>
        
        {/* Results Grid or Generate Button */}
        {!results ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-muted/50 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-6 h-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Generate and compare prompts across {modelsToCompare.length} AI models
            </p>
            <Button 
              onClick={handleCompare} 
              disabled={loading || !prompt?.trim()}
              className="gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Comparing...
                </>
              ) : (
                <>
                  <Columns className="w-4 h-4" />
                  Compare Results ({modelsToCompare.length * 2} credits)
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground/70 mt-3 italic">
              Better comparisons = better results
            </p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            {/* Results Grid */}
            <div className={`grid gap-3 ${
              modelsToCompare.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' : 
              modelsToCompare.length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 
              'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
            }`}>
              {modelsToCompare.map((modelId, index) => {
                const model = AI_MODELS.find(m => m.id === modelId);
                const result = results.comparisons?.[modelId];
                const shouldBlur = index >= (results.blur_after_index || modelsToCompare.length);
                
                if (shouldBlur) {
                  return (
                    <BlurredResultCard 
                      key={modelId}
                      model={model}
                      onClick={() => setShowUpgradeModal(true)}
                    />
                  );
                }
                
                return (
                  <ResultCard
                    key={modelId}
                    model={model}
                    result={result}
                    isSelected={modelId === selectedModel}
                    onCopy={copyToClipboard}
                    copied={copied[modelId]}
                  />
                );
              })}
            </div>
            
            {/* Pro upsell for Growth users */}
            {userPlan === 'growth' && (
              <motion.div 
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  <span className="text-xs">
                    Upgrade to Pro for up to 4 models side-by-side
                  </span>
                </div>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-7 text-xs"
                  onClick={() => setShowUpgradeModal(true)}
                >
                  Upgrade
                </Button>
              </motion.div>
            )}
            
            <p className="text-xs text-muted-foreground/70 text-center italic">
              Better comparisons = better results
            </p>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default CompareResults;
